import React from 'react';

// Components and Others
import PropTypes from 'prop-types';
import StringUtils from 'lib/StringUtils';
import ButtonStyles from 'styles/theme/Button';
import { makeStyles } from '@material-ui/core/styles';
import {
    DialogContent, Dialog, Grid,
} from '@material-ui/core';
import DialogAppBar from 'components/widgets/modal/DialogAppBar';
import DialogActions from 'components/widgets/modal/DialogActions';

const useStyles = makeStyles((theme) => ({
    dialogContent: {
        padding: theme.spacing(3, 1.5),
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    row: {
        flexDirection: 'column',
        display: 'flex',
        alignItems: 'center',
        '& .title-address': {
            display: 'block',
            marginBottom: '8px',
        },
    },
    warning: {
        color: theme.palette.warning.main,
        marginTop: theme.spacing(1),
        textAlign: 'center',
    },
    ...ButtonStyles.getStyle(theme),
}));

export const buildSuggestedAddressInput = ({
    streetAddress, city, state, zipCode,
}) => {
    if (StringUtils.isEmpty(streetAddress) || StringUtils.isEmpty(city) || StringUtils.isEmpty(state)) {
        return null;
    }

    const input = {
        streetAddress: streetAddress.trim(),
        city: city.trim(),
        state: state.trim(),
    };

    if (!StringUtils.isEmpty(zipCode)) {
        input.zipCode = String(zipCode).trim();
    }

    return input;
};

export const formatSuggestedZip = (address) => {
    if (StringUtils.isEmpty(address?.ZIPCode)) return '';
    return String(address.ZIPCode).trim().slice(0, 5);
};

const hasAddressText = (value) => String(value || '').trim().length > 0;

export const mapSuggestedAddressToForm = (suggestedAddress = {}, currentAddress = {}) => ({
    address: suggestedAddress.streetAddress || '',
    address2: hasAddressText(suggestedAddress.secondaryAddress)
        ? suggestedAddress.secondaryAddress
        : (currentAddress.address2 || ''),
    state: suggestedAddress.state || '',
    zipCode: formatSuggestedZip(suggestedAddress),
    city: suggestedAddress.city || '',
});

const UNCONFIRMED_COMPONENTS_WARNING = 'Address has unconfirmed components';

const SuggestAddress = ({
    open, onClose, currentAddress, suggestedAddress,
    onChangeAddress, warnings,
}) => {
    const classes = useStyles();
    const hasSuggestion = hasAddressText(suggestedAddress?.streetAddress);
    const showUnconfirmedWarning = (warnings || []).some((warning) => (
        String(warning).toLowerCase() === UNCONFIRMED_COMPONENTS_WARNING.toLowerCase()
    ));
    const suggestedZip = formatSuggestedZip(suggestedAddress);

    const onUseOriginalAddress = () => onChangeAddress({
        address: currentAddress.address1,
        address2: currentAddress.address2,
        state: currentAddress.state,
        zipCode: currentAddress.zipCode,
        city: currentAddress.city,
    });

    const onUseSuggestedAddress = () => {
        if (!hasSuggestion) return;
        onChangeAddress(mapSuggestedAddressToForm(suggestedAddress, currentAddress));
    };

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="sm"
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogAppBar title="Address Verification Confirmation" onClose={onClose} iconSize="sm" />
            <DialogContent className={classes.dialogContent}>
                <Grid container className="am-form">
                    <Grid item xs={6} className={classes.row}>
                        <span className="title-address">Original address:</span>
                        <span>
                            {currentAddress.address1}
                            ,
                        </span>
                        {currentAddress.address2 && <span>{currentAddress.address2}</span>}
                        <span>{`${currentAddress.city}, ${currentAddress.state}, ${currentAddress.zipCode}`}</span>
                    </Grid>
                    <Grid item xs={6} className={classes.row}>
                        <span className="title-address">Suggested address:</span>
                        {!hasSuggestion && (
                            <span className={classes.warning}> No address suggested</span>
                        )}
                        {hasSuggestion && (
                            <>
                                <span>
                                    {suggestedAddress.streetAddress}
                                    ,
                                </span>
                                {suggestedAddress.secondaryAddress && (
                                    <span>{suggestedAddress.secondaryAddress}</span>
                                )}
                                <span>{`${suggestedAddress.city}, ${suggestedAddress.state}, ${suggestedZip}`}</span>
                            </>
                        )}
                        {showUnconfirmedWarning && (
                            <span className={classes.warning}>{UNCONFIRMED_COMPONENTS_WARNING}</span>
                        )}
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions
                titlePrimary="Use suggested address"
                titleSecondary="Use original address"
                onClickSecondary={onUseOriginalAddress}
                onClickPrimary={onUseSuggestedAddress}
                classSecondaryButton={classes.containedGreen}
                classPrimaryButton={classes.containedPrimary}
                disablePrimaryButton={!hasSuggestion}
            />
        </Dialog>
    );
};

SuggestAddress.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onChangeAddress: PropTypes.func.isRequired,
    currentAddress: PropTypes.object.isRequired,
    suggestedAddress: PropTypes.object.isRequired,
    warnings: PropTypes.arrayOf(PropTypes.string),
};

SuggestAddress.defaultProps = {
    warnings: [],
};

export default SuggestAddress;
